import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usuarioService, Usuario, UsuarioRequest } from '../../services/usuario';
import SelectFuncionario from '../funcionario/SelectFuncionario';
import SelectPerfilAcesso from '../perfilAcesso/SelectPerfilAcesso';

interface UsuarioFormProps {
  isEdit?: boolean;
}

const UsuarioForm: React.FC<UsuarioFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    idFuncionario: 0,
    usuario: '',
    senha: '',
    idIdentityRole: '',
  });
  const [errors, setErrors] = useState({
    idFuncionario: '',
    usuario: '',
    senha: '',
    idIdentityRole: '',
  });

  // Buscar usuário para edição
  const { data: usuario, isLoading } = useQuery({
    queryKey: ['usuario', id],
    queryFn: () => usuarioService.getById(id!),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: UsuarioRequest) => usuarioService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      navigate('/usuarios');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar usuário:', error);
    },
  });

  useEffect(() => {
    if (usuario && isEdit) {
      setFormData({
        idFuncionario: usuario.idFuncionario || 0,
        usuario: usuario.usuario,
        senha: usuario.senha || '',
        idIdentityRole: usuario.idIdentityRole || '',
      });
    }
  }, [usuario, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = {
      idFuncionario: '',
      usuario: '',
      senha: '',
      idIdentityRole: '',
    };
    let isValid = true;

    if (!formData.idFuncionario) {
      newErrors.idFuncionario = 'Funcionário é obrigatório';
      isValid = false;
    }

    if (!formData.usuario.trim()) {
      newErrors.usuario = 'Nome de usuário é obrigatório';
      isValid = false;
    } else if (formData.usuario.length > 50) {
      newErrors.usuario = 'Nome de usuário não deve possuir mais que 50 caracteres';
      isValid = false;
    }

    if (!formData.senha.trim()) {
      newErrors.senha = 'Senha é obrigatória';
      isValid = false;
    } else if (formData.senha.length < 6) {
      newErrors.senha = 'A senha deve conter no mínimo 6 caracteres';
      isValid = false;
    } else if (!/[a-z]/.test(formData.senha)) {
      newErrors.senha = 'A senha deve conter ao menos 1 letra minúscula';
      isValid = false;
    } else if (!/[A-Z]/.test(formData.senha)) {
      newErrors.senha = 'A senha deve conter ao menos 1 letra maiúscula';
      isValid = false;
    }

    if (!formData.idIdentityRole) {
      newErrors.idIdentityRole = 'Perfil de acesso é obrigatório';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSelectChange = (field: string) => (value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleStringSelectChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const usuarioData: UsuarioRequest = {
        id: isEdit ? id : undefined,
        idFuncionario: formData.idFuncionario,
        usuario: formData.usuario.trim(),
        senha: formData.senha,
        idIdentityRole: formData.idIdentityRole,
      };
      
      saveMutation.mutate(usuarioData);
    }
  };

  const handleClear = () => {
    setFormData({
      idFuncionario: 0,
      usuario: '',
      senha: '',
      idIdentityRole: '',
    });
    setErrors({
      idFuncionario: '',
      usuario: '',
      senha: '',
      idIdentityRole: '',
    });
  };

  const handleBack = () => {
    navigate('/usuarios');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Nome de usuário"
              value={formData.usuario}
              onChange={handleInputChange('usuario')}
              error={!!errors.usuario}
              helperText={errors.usuario}
              slotProps={{
                htmlInput: { maxLength: 50 }
              }}
              required
            />
            
            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.senha}
              onChange={handleInputChange('senha')}
              error={!!errors.senha}
              helperText={errors.senha}
              slotProps={{
                htmlInput: { maxLength: 100 },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
              required
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <SelectFuncionario
              value={formData.idFuncionario}
              onChange={handleSelectChange('idFuncionario')}
              error={!!errors.idFuncionario}
              helperText={errors.idFuncionario}
              required
            />
            
            <SelectPerfilAcesso
              value={formData.idIdentityRole.toString()}
              onChange={handleStringSelectChange('idIdentityRole')}
              error={!!errors.idIdentityRole}
              helperText={errors.idIdentityRole}
              required
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default UsuarioForm;
